import { EnvironmentInjector, inject } from '@angular/core';

import { LoggerService } from './logger.service';
import { convertObjectToString } from 'app/shared/utils/string.util';
import { ResourceCreateEvent } from './logger.resource-create-event';
import { EventRow } from './logger.event-row';
import { loggerConstants } from './logger.constants';
import { scrubPII } from 'app/extension/api/utils';
import { AuthService } from 'app/services/authentication/auth.service';

export function buildLoggerHelperInContext(){

    const environmentInjector: EnvironmentInjector = inject(EnvironmentInjector);

    const requestStatusCodesUserErrors: number[] = [0];
    const sandboxRunUserErrors: string[] = ['SandboxPipelineAlreadyCreated'];
    const gitUserErrors: string[] = ['TF401028', 'TF402455', 'TF401174'];
    const gitExpectedErrorsStatusCodes: number[] = requestStatusCodesUserErrors.concat(404);
    const warningClassifications: string[] = ['FeatureNotEnabled'];

    return {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        logRequestError(className: string, message: string, error: any, properties?: { [key: string]: string }): void {
            const messageToLog = `${message}, error: ${this.tryToParseErrorForLogging(error)}`;
            const warningCondition = () => ((error && requestStatusCodesUserErrors.includes(error.status)) || this.checkErrorIsWarning(messageToLog));
            this.logErrorInternal(className, messageToLog, warningCondition, properties);
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        logRequestWarning(className: string, message: string, error: any, properties?: {
            [key: string]: string
        }): void {
            const messageToLog = `${message}, error: ${this.tryToParseErrorForLogging(error)}`;
            this.logErrorInternal(className, messageToLog, () => true, properties);
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        logGitError(className: string, message: string, error: any, properties?: { [key: string]: string }): void {
            error = this.parseErrorIfNeeded(error);
            const messageToLog = `${message}, error: ${this.tryToParseErrorForLogging(error)}`;
            const warningCondition = () => error &&
                (error.message && gitUserErrors.find((userError) => error.message.startsWith(userError)) !== undefined)
                || gitExpectedErrorsStatusCodes.includes(error.status);
            this.logErrorInternal(className, messageToLog, warningCondition, properties);
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        logSandboxRunError(className: string, message: string, error: any, properties?: {
            [key: string]: string
        }): void {
            const requestStatus = error && error.status;
            error = this.parseErrorIfNeeded(error);
            const messageToLog = `${message}, error: ${this.tryToParseErrorForLogging(error)}`;
            const warningCondition = () => error && (sandboxRunUserErrors.includes(error.code) || requestStatusCodesUserErrors.includes(requestStatus));
            this.logErrorInternal(className, messageToLog, warningCondition, properties);
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        logSandboxRunWarning(className: string, message: string, error: any, properties?: {
            [key: string]: string
        }): void {
            error = this.parseErrorIfNeeded(error);
            const messageToLog = `${message}, error: ${this.tryToParseErrorForLogging(error)}`;
            const warningCondition = () => true;
            this.logErrorInternal(className, messageToLog, warningCondition, properties);
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        logResourceCreation(resourceType: string, resourceName: string, resourceId: string, success: boolean, properties?: Record<string, any>): void {
            const loggerService = environmentInjector.get(LoggerService);

            const event = new ResourceCreateEvent(
                resourceName,
                resourceId,
                resourceType,
                loggerService.urlProvider.subscriptionId,
                success,
                properties
            );
            loggerService.logEvent(event);
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        logVNetIRDataFlowDebug(irName: string, vnetName: string): void {
            const loggerService = environmentInjector.get(LoggerService);

            const factoryId = loggerService.urlProvider.getResourceId();
            const event = new EventRow(`${loggerConstants.Event_VNetIRDataFlowDebug}.On`, {
                ['factory']: factoryId,
                ['ir']: `${factoryId}/integrationruntimes/${irName}`,
                ['vnet']: vnetName
            });
            loggerService.logEvent(event);
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        logIRInteractiveAuthoringEnabled(irName: string): void {
            const loggerService = environmentInjector.get(LoggerService);

            const factoryId = loggerService.urlProvider.getResourceId();
            const event = new EventRow(`${loggerConstants.Event_IRInteractiveAuthoring}.On`, {
                ['subscriptionId']: loggerService.urlProvider.subscriptionId,
                ['ir']: `${factoryId}/integrationruntimes/${irName}`
            });
            loggerService.logEvent(event);
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        logErrorInternal(className: string, message: string, warningCondition: () => boolean, properties?: {
            [key: string]: string
        }): void {
            const loggerService = environmentInjector.get(LoggerService);

            if (warningCondition()) {
                loggerService.warn(className, message, properties);
            } else {
                loggerService.error(className, message, properties);
            }
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        parseErrorIfNeeded(error: any): any {
            try {
                error = error.json();
            } catch {
                // Error is already parsed
            }
            return error;
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        tryToParseErrorForLogging(error: any): string {

            let stringifiedError = String(error);
            try {
                stringifiedError = convertObjectToString(error);
            } catch {
                // Some requests return invalid structures that we cannot parse, in that case, just try to stringify the whole value
            }
            if (__IS_A365__) {
                const authService = environmentInjector.get(AuthService);

                // Only scrubbing in A365 for now.
                stringifiedError = scrubPII(stringifiedError, authService);
            }
            return stringifiedError;
        },
        checkErrorIsWarning(error: string): boolean {
            return !!warningClassifications.find((warningMessage) => error.includes(warningMessage));
        }
    };
}

export type LoggerHelper = ReturnType<typeof buildLoggerHelperInContext>;

